import React from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { SelectLabel, CheckboxLabel, InputLabel, Input, ToggleContainer } from '@weezevent/nacre';
import { TransitionActionCardContent } from './ActionCard';

import useAppContext from '../../../../../../context/useAppContext';
import EventModel from '../../../../../../models/eventModel';
import RateModel from '../../../../../../models/rateModel';

import Styles from '../transitionDetails.module.css';

const recipientOptions = [
    {
        text: <Trans i18nKey={'ticket.moderation.statusesTransitions.transition.steps.actions.recipientAttendeeOption'} />,
        value: 'attendee'
    },
    {
        text: <Trans i18nKey={'ticket.moderation.statusesTransitions.transition.steps.actions.recipientBuyerOption'} />,
        value: 'buyer'
    }
];

export const EmailAction = ({ action, index, handleAction, options }) => {
    const { t } = useTranslation();

    return (
        <TransitionActionCardContent action={action} index={index} handleAction={handleAction} options={options}>
            <SelectLabel
                useNew
                value={action.to}
                options={recipientOptions}
                onChange={value => handleAction('to', value, index)}
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.recipientLabel')}
                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.recipientLabel')}
            />
            <div className={Styles['action-card-input']}>
                <InputLabel label={t('ticket.moderation.statusesTransitions.transition.steps.actions.senderLabel')} />
                <Input
                    placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.senderLabel')}
                    onChange={({ target: { value } }) => handleAction('identity_id', value, index)}
                    value={action.identity_id}
                />
            </div>
            <div className={Styles['action-card-input']}>
                <InputLabel label={t('ticket.moderation.statusesTransitions.transition.steps.actions.mailTemplateLabel')} />
                <Input
                    placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.mailTemplateLabel')}
                    onChange={({ target: { value } }) => handleAction('template_id', value, index)}
                    value={action.template_id}
                />
            </div>
            <ToggleContainer
                title={t('ticket.moderation.statusesTransitions.transition.steps.actions.sendTicketLabel')}
                subtitle={t('ticket.moderation.statusesTransitions.transition.steps.actions.sendTicketDesc')}
                onChange={({ target: { checked } }) => handleAction('send_ticket', checked, index)}
                value={action.send_ticket}
                toggle={action.send_ticket}
            />

            <ToggleContainer
                title={t('ticket.moderation.statusesTransitions.transition.steps.actions.replayableActionLabel')}
                subtitle={t('ticket.moderation.statusesTransitions.transition.steps.actions.replayableActionDesc')}
                onChange={({ target: { checked } }) => handleAction('allow_replay', checked, index)}
                value={action.allow_replay}
                toggle={action.allow_replay}
            />
        </TransitionActionCardContent>
    );
};

export const AttendeeCreationAction = ({ action, index, handleAction, options }) => {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();
    const [events] = EventModel.useApiModel({ organizationId: organization.id, query: 'publication_status__in=draft,published', cache: false, allow_cache: false }, [
        organization.id
    ]);
    const eventsOptions = React.useMemo(() => events?.map(e => e.toOptions()) || [], [events]);
    const [selectedEventId, setSelectedEventId] = React.useState(null);
    const [rates] = RateModel.useApiModel(
        { organizationId: organization.id, eventId: selectedEventId, launch: Boolean(organization.id && selectedEventId), cache: false, allow_cache: false },
        [organization.id, selectedEventId]
    );
    const ratesOptions = React.useMemo(() => rates?.map(rate => rate.toOption()) || [], [rates]);

    return (
        <TransitionActionCardContent action={action} index={index} handleAction={handleAction} options={options}>
            <SelectLabel
                useNew
                value={action.destination_event}
                options={eventsOptions}
                onChange={value => {
                    handleAction('destination_event', value, index);
                    setSelectedEventId(value);
                }}
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.events_label')}
                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.events_placeholder')}
            />
            <SelectLabel
                useNew
                value={action.destination_rate}
                disabled={!selectedEventId && !rates}
                options={rates ? ratesOptions : []}
                onChange={value => handleAction('destination_rate', value, index)}
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.rates_label')}
                placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.rates_placeholder')}
            />
            <div>{t('ticket.moderation.statusesTransitions.transition.steps.actions.form_answers_label')}</div>
            <CheckboxLabel
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.form_answers')}
                onChange={({ target: { checked } }) => handleAction('keep_form_answers', checked, index)}
                checked={action.keep_form_answers}
                toggle={action.keep_form_answers}
            />

            <CheckboxLabel
                label={t('ticket.moderation.statusesTransitions.transition.steps.actions.membership_code')}
                onChange={({ target: { checked } }) => handleAction('keep_membership_code', checked, index)}
                checked={action.keep_membership_code}
                toggle={action.keep_membership_code}
            />
        </TransitionActionCardContent>
    );
};

export const FormFillAction = ({ action, index, handleAction, options }) => {
    const { t } = useTranslation();
    return (
        <TransitionActionCardContent action={action} index={index} handleAction={handleAction} options={options}>
            <div className={Styles['action-card-input']}>
                <InputLabel label={t('ticket.moderation.statusesTransitions.transition.steps.actions.formFieldLabel')} />
                <Input
                    name="sender"
                    placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.formFieldLabel')}
                    onChange={({ target: { value } }) => handleAction('form_field', value, index)}
                    value={action.form_field}
                />
            </div>
            <div className={Styles['action-card-input']}>
                <InputLabel label={t('ticket.moderation.statusesTransitions.transition.steps.actions.defaultValueLabel')} />
                <Input
                    name="mailTemplate"
                    placeholder={t('ticket.moderation.statusesTransitions.transition.steps.actions.defaultValueLabel')}
                    onChange={({ target: { value } }) => handleAction('default_value', value, index)}
                    value={action.default_value}
                />
            </div>
        </TransitionActionCardContent>
    );
};
